/**
 * "DISPLAY SETTINGS" - the prototype's round button in the header and the small panel it opens
 * (`moonx-authors-prototype/MoonX App.dc.html`, `settingsOpen`): Theme - Auto, Light or Dark.
 *
 * Auto is the default, as it already was: both apps follow the system setting
 * (`defaultColorScheme="auto"`, web/ui/src/AppProviders.tsx). The owner ruled on 2026-10-01 to keep
 * that and add this switch (design 15, decision 3). Mantine keeps the choice in the browser, so it
 * survives a reload. The prototype's second setting, "Moon rendering", draws moons this cabinet
 * does not draw, so it is not here.
 *
 * A change is made inside `withAtomicThemeSwap`, the one place that turns transitions off while the
 * colours swap (web/ui/src/theme/color-scheme.ts), so the page changes at once rather than fading
 * element by element.
 *
 * LANGUAGE sits under Theme (design 29, W5): kept in the browser like the theme, never written to
 * the profile (./cabinet-language.ts, ./LanguagePicker.tsx).
 */
import { Popover, SegmentedControl, Stack, Text, UnstyledButton } from '@mantine/core';
import { useMantineColorScheme } from '@mantine/core';
import { Monitor, Moon, Settings, Sun, withAtomicThemeSwap } from '@moonx/ui';
import type { ReactNode } from 'react';
import { useTranslation } from '../../i18n';
import { LanguageSelect } from './LanguagePicker';
import classes from './cabinet-frame.module.css';

type SchemeChoice = 'auto' | 'light' | 'dark';

function isSchemeChoice(value: string): value is SchemeChoice {
  return value === 'auto' || value === 'light' || value === 'dark';
}

function choiceLabel(icon: ReactNode, words: string): ReactNode {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
      {icon}
      {words}
    </span>
  );
}

export function DisplaySettings() {
  const { colorScheme, setColorScheme } = useMantineColorScheme();
  const { t } = useTranslation('shell');

  return (
    <Popover position="bottom-end" width={280} shadow="md" radius="lg">
      <Popover.Target>
        <UnstyledButton className={classes.roundControl} aria-label={t('display.title')}>
          <Settings size={16} aria-hidden />
        </UnstyledButton>
      </Popover.Target>
      <Popover.Dropdown>
        <Stack gap="sm">
          <Text size="sm" fw={800}>
            {t('display.title')}
          </Text>
          <Stack gap={6}>
            <Text size="xs" fw={700} c="dimmed" id="display-settings-theme">
              {t('display.theme')}
            </Text>
            <SegmentedControl
              fullWidth
              size="xs"
              radius="xl"
              aria-labelledby="display-settings-theme"
              value={colorScheme}
              onChange={(value) => {
                if (!isSchemeChoice(value)) {
                  return;
                }
                withAtomicThemeSwap(() => {
                  setColorScheme(value);
                });
              }}
              data={[
                {
                  value: 'auto',
                  label: choiceLabel(<Monitor size={13} aria-hidden />, t('display.auto')),
                },
                {
                  value: 'light',
                  label: choiceLabel(<Sun size={13} aria-hidden />, t('display.light')),
                },
                {
                  value: 'dark',
                  label: choiceLabel(<Moon size={13} aria-hidden />, t('display.dark')),
                },
              ]}
            />
            <Text size="xs" c="dimmed">
              {t('display.autoHint')}
            </Text>
          </Stack>
          <Stack gap={6}>
            <Text size="xs" fw={700} c="dimmed" id="display-settings-language">
              {t('language.label')}
            </Text>
            <LanguageSelect aria-labelledby="display-settings-language" />
          </Stack>
        </Stack>
      </Popover.Dropdown>
    </Popover>
  );
}
